import { Button } from '@design-system';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import {
  ForwardedRef,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { useProductCheckoutLink } from '../../../features/checkout/hooks/useProductCheckoutLink';
import { purchasingState } from '../../../helpers/purchasing';
import { useFlow } from '../../../providers';
import type {
  DrawerFormType,
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { TrackBuynowClick } from '../../../utils/analytics';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { renderShopifyArtistName } from '../../../utils/artists';
import { framedByDefault } from '../../../utils/frames';
import { ProductGatedRelease } from '../../Content/GatedRelease/ProductGatedRelease';
import { CrossSellContext } from '../CrossSell/CrossSellContext';
import { SkeletonCta } from './ctas/Skeleton';
import { TimedPurchaseCta } from './ctas/TimedPurchase';
import { FewLeft } from './FewLeft';
import { NFTPrintServiceForm } from './NFTPrintServiceForm';

const ReviewCheckoutDrawer = dynamic(
  () => import('../ReviewCheckoutDrawer/ReviewCheckoutDrawer'),
  { ssr: false },
);

type Props<T extends ProductPurchaseDetails> = {
  layout: 'dialog' | 'drawer';
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
};

const PurchaseForm = forwardRef(
  <T extends ProductPurchaseDetails>(
    { layout, product, purchasingController }: Props<T>,
    ref: ForwardedRef<{
      onBuyNowClick: () => void;
    }>,
  ) => {
    const { t } = useTranslation('product');
    const { goToStep } = useFlow();
    const { crossSellOffered } = useContext(CrossSellContext);
    const [visibleForm, setVisibleForm] = useState<DrawerFormType>();
    const [emitter] = useAnalyticsEmitter();
    const router = useRouter();
    const checkoutLink = useProductCheckoutLink();

    const frameIncluded = framedByDefault(
      purchasingController.product?.framingOffered,
    );

    useEffect(() => {
      // if there's a pre-selected collection set, show the releases form
      if (!visibleForm && purchasingController.isPreSelectedCollectionSet) {
        setVisibleForm('releases');
        /**
         * Showing the releases form with pre-selected options only needed when user lands on the page.
         * We have to make sure form doesn't re-open once it was closed
         */
        purchasingController.setIsPreSelectedCollectionSet(false);
      }
    }, [visibleForm, purchasingController]);

    const onBuyNowClick = async () => {
      await TrackBuynowClick({
        canBeFramed: !!purchasingController.product?.separateFrameAvailable,
        crossSellOffered,
        frameGlazingName: purchasingController.frame?.variant.title,
        frameUpgradeAvailable: Boolean(purchasingController.frameOptions),
        isFramed: !!purchasingController.frame,
        price: purchasingController.product?.price,
        product,
        quantity: purchasingController.quantity,
      });

      if (checkoutLink) {
        void router.push(checkoutLink);
        return;
      }

      if (purchasingController.collectionSet) {
        if (layout === 'dialog') {
          goToStep('releases');
        } else {
          setVisibleForm('releases');
        }
        return;
      }

      if (purchasingController.frameOptions?.length) {
        if (layout === 'dialog') {
          goToStep('frame');
        } else {
          setVisibleForm('frame');
        }
        return;
      }

      const showReviewScreen =
        purchasingController.product?.allowMultipleProducts ||
        purchasingController.product?.noOrderCancellation ||
        crossSellOffered;

      if (showReviewScreen) {
        if (layout === 'dialog') {
          goToStep('review');
        } else {
          setVisibleForm('review');
        }
        return;
      }

      purchasingController.goToCheckout();
    };

    useImperativeHandle(ref, () => ({
      onBuyNowClick,
    }));

    if (purchasingController.product === null) {
      return <SkeletonCta />;
    }

    const state = purchasingState({
      releaseType: product.releaseType,
      closingDate: product.closingDate,
      unlocked: purchasingController.unlocked,
      available: purchasingController?.product?.available,
      tags: purchasingController?.product?.tags,
    });

    const renderCta = () => {
      if (product.isNFTPrintService) {
        return (
          <NFTPrintServiceForm
            product={product}
            purchasingController={purchasingController}
            release={purchasingController.release}
            unlockCode={purchasingController.unlockCode}
          />
        );
      }

      if (product.releaseType === 'timed-release') {
        return (
          <TimedPurchaseCta
            onBuyNowClick={onBuyNowClick}
            product={product}
            purchasingController={purchasingController}
          />
        );
      }

      return (
        <Button
          disabled={!purchasingController.canPurchase}
          fullWidth
          label={t('goToCheckout')}
          loading={purchasingController.checkoutLoading}
          onClick={onBuyNowClick}
          variant="primary"
        />
      );
    };

    if (state === 'AVAILABLE') {
      return (
        <>
          <ProductGatedRelease
            product={product}
            purchasingController={purchasingController}
          >
            <FewLeft
              className="mb-6"
              tags={purchasingController.product?.tags || []}
            />
            {renderCta()}
          </ProductGatedRelease>
          {layout === 'drawer' && (
            <ReviewCheckoutDrawer
              analyticsEmitter={(name, action, properties, options) => {
                emitter(
                  `checkout review step ${name}`,
                  action,
                  {
                    'product_title': product?.title,
                    'artist name': renderShopifyArtistName(
                      purchasingController.product?.artist ?? '',
                    ),
                    'product_medium': product?.medium,
                    'product_price': purchasingController.product?.price,
                    'product_edition_size': product?.editionSize,
                    'shopify_product_id': product?.id,
                    'shopify_product_variant_id':
                      purchasingController.product?.shopifyVariantId,
                    'product_can_be_framed': Boolean(
                      (purchasingController.frameOptions?.length ?? 0) > 0,
                    ),
                    'product_frame_upgrade_available': Boolean(
                      purchasingController.frameOptions,
                    ),
                    'product_frame_chosen': Boolean(purchasingController.frame),
                    'product_framed_by_default': frameIncluded,
                    ecommerce: {
                      items: [
                        {
                          item_id: product.id[0].id,
                          item_name: product.title,
                          item_brand: renderShopifyArtistName(
                            purchasingController.product?.artist ?? '',
                          ),
                          item_category: product.medium?.join(','),
                          price: purchasingController.product?.price,
                          quantity: purchasingController.quantity,
                        },
                      ],
                    },
                    ...properties,
                  },
                  options,
                );
              }}
              overlayOtherDrawers
              product={product}
              purchasingController={purchasingController}
              setVisibleForm={(form) => setVisibleForm(form)}
              showCrossSell={crossSellOffered}
              visibleForm={visibleForm}
            />
          )}
        </>
      );
    }

    return null;
  },
);

export default PurchaseForm;
